import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

import SearchInput from "../inputs/SearchInput/SearchInput";

interface Props {
  isVisible: boolean;
  isSingle?: boolean;
  placeholder?: string;
  title: string;
  disabled?: boolean;
  options: {
    value: string | number;
    label: string;
  }[];
  onApply: (e: { value: string | number; label: string }[]) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const FilterMultiselect: React.FC<Props> = ({
  isVisible,
  isSingle,
  placeholder,
  options,
  title,
  disabled,
  onApply,
  onClose,
  anchorRef,
}) => {
  const { t } = useTranslation();

  const [selectedItems, setSelectedItems] = useState<
    {
      value: string | number;
      label: string;
    }[]
  >([]);
  const [itemsToRender, setItemsToRender] = useState<
    {
      value: string | number;
      label: string;
    }[]
  >([]);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    const result: { value: string | number; label: string }[] = [];
    options.forEach((el) => {
      if (el.label?.toLowerCase().includes(searchValue?.toLowerCase())) {
        result.push(el);
      }
    });
    setItemsToRender(result);
  }, [searchValue, options]);

  useEffect(() => {
    if (!isVisible || !anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();

    setPosition({
      top: rect.top + window.scrollY,
      left: rect.right + 10,
    });

    setReady(true);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setReady(false);
    }
  }, [isVisible]);

  const handleApply = () => {
    onApply(selectedItems);
    setSelectedItems([]);
    onClose();
  };
  const handleReset = () => {
    setSelectedItems([]);
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleItemClick = (e: { value: string | number; label: string }) => {
    if (selectedItems?.includes(e)) {
      setSelectedItems(selectedItems.filter((el) => el !== e));
    } else {
      setSelectedItems(isSingle ? [e] : [...(selectedItems ?? []), e]);
    }
  };

  return createPortal(
    <div
      className={`filter-multiselect-wrapper visible ${ready ? "ready" : ""}`}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
      }}
    >
      <div className="filter-multiselect-header">
        <div className="title">{title}</div>
        <div className="buttons">
          <div className="apply" onClick={handleApply}>
            {t("apply")}
          </div>
          <div className="reset" onClick={handleReset}>
            {t("reset")}
          </div>
        </div>
      </div>

      <div className="filter-multiselect-search">
        <SearchInput
          placeholder={placeholder}
          value={searchValue}
          onChange={onSearch}
          isSmall
        />
      </div>

      <div className="filter-multiselect-list">
        {itemsToRender.map((el) => (
          <div
            key={el.value}
            className={`item ${selectedItems.includes(el) ? "selected" : ""}`}
            onClick={() => handleItemClick(el)}
          >
            {el.label}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default FilterMultiselect;
